export const generateRecommendations = (scores, domains, clientInfo) => {
  return generatePersonalizedRecommendations(scores, domains, clientInfo)
}

export const generatePersonalizedRecommendations = (scores, domains, clientInfo) => {
  const values = Array.isArray(scores) ? scores : Object.values(scores)
  const averageScore = values.reduce((sum, val) => sum + val, 0) / values.length
  
  const criticalDomains = []
  const strongDomains = []
  const improvements = []

  values.forEach((score, index) => {
    const domain = domains[index]
    if (score <= 2) {
      criticalDomains.push({
        domain: domain.name,
        shortName: domain.shortName,
        score,
        urgency: 'high'
      })
    } else if (score >= 7) {
      strongDomains.push({
        domain: domain.name,
        shortName: domain.shortName,
        score,
        potential: 'leverage'
      })
    } else if (score <= 5) {
      improvements.push({
        domain: domain.name,
        shortName: domain.shortName,
        score,
        priority: score <= 3 ? 'high' : 'medium'
      })
    }
  })

  return {
    overview: {
      averageScore,
      totalDomains: domains.length,
      criticalCount: criticalDomains.length,
      strongCount: strongDomains.length,
      improvementCount: improvements.length
    },
    criticalDomains,
    strongDomains,
    improvements,
    strategicRecommendations: generateStrategicRecommendations(averageScore, criticalDomains, strongDomains),
    actionPlan: generateActionPlan(criticalDomains, improvements, strongDomains),
    nextSteps: generateNextSteps(averageScore, criticalDomains.length, strongDomains.length)
  }
}

const generateStrategicRecommendations = (averageScore, criticalDomains, strongDomains) => {
  const recommendations = []

  if (criticalDomains.length >= 3) {
    recommendations.push({
      type: 'urgent',
      title: 'Plan de redressement urgent',
      description: 'Plusieurs domaines critiques nécessitent une intervention immédiate. Priorisez la stabilisation avant la croissance.',
      priority: 1
    })
  }

  if (strongDomains.length >= 3 && criticalDomains.length <= 1) {
    recommendations.push({
      type: 'growth',
      title: 'Capitalisation sur les forces',
      description: 'Vos domaines forts constituent une base solide. Utilisez-les pour compenser les faiblesses et accélérer le développement.',
      priority: 2
    })
  }

  if (averageScore >= 7) {
    recommendations.push({
      type: 'excellence',
      title: 'Optimisation vers l\'excellence',
      description: 'Performance globale solide. Concentrez-vous sur l\'innovation et l\'optimisation des processus.',
      priority: 2
    })
  } else if (averageScore <= 4) {
    recommendations.push({
      type: 'restructuring',
      title: 'Restructuration globale',
      description: 'La performance générale nécessite une approche structurée d\'amélioration sur plusieurs fronts.',
      priority: 1
    })
  }

  return recommendations.sort((a, b) => a.priority - b.priority)
}

const generateActionPlan = (criticalDomains, improvements, strongDomains) => {
  const actions = []

  // Actions urgentes pour les domaines critiques
  criticalDomains.forEach(domain => {
    actions.push({
      domain: domain.shortName,
      action: getSpecificAction(domain.shortName, 'critical'),
      timeline: 'Immédiat (1-2 mois)',
      priority: 'Haute'
    })
  })

  // Actions d'amélioration
  improvements.slice(0, 2).forEach(domain => {
    actions.push({
      domain: domain.shortName,
      action: getSpecificAction(domain.shortName, 'improvement'),
      timeline: 'Court terme (3-6 mois)',
      priority: domain.priority === 'high' ? 'Haute' : 'Moyenne'
    })
  })

  // Optimisation des forces
  strongDomains.slice(0, 2).forEach(domain => {
    actions.push({
      domain: domain.shortName,
      action: getSpecificAction(domain.shortName, 'optimization'),
      timeline: 'Moyen terme (6-12 mois)',
      priority: 'Moyenne'
    })
  })

  return actions
}

const getSpecificAction = (domainShortName, actionType) => {
  const actions = {
    Finance: {
      critical: 'Audit financier urgent et mise en place d\'un contrôle de gestion renforcé',
      improvement: 'Formation en gestion financière et mise en place d\'outils de suivi',
      optimization: 'Optimisation de la structure financière et stratégies d\'investissement'
    },
    Stratégie: {
      critical: 'Redéfinition urgente de la stratégie d\'entreprise avec accompagnement expert',
      improvement: 'Clarification de la vision et mise en place d\'objectifs SMART',
      optimization: 'Développement de nouvelles opportunités stratégiques'
    },
    RH: {
      critical: 'Audit RH et mise en place d\'une politique de gestion des talents',
      improvement: 'Formation managériale et amélioration de la communication interne',
      optimization: 'Développement des compétences et politique de fidélisation'
    },
    Admin: {
      critical: 'Restructuration des processus administratifs et mise en conformité',
      improvement: 'Digitalisation des processus et formation aux outils',
      optimization: 'Automatisation et amélioration continue des processus'
    },
    Commercial: {
      critical: 'Refonte de la stratégie commerciale et formation des équipes',
      improvement: 'Développement des compétences commerciales et prospection',
      optimization: 'Diversification des canaux et optimisation de la performance'
    },
    Production: {
      critical: 'Audit de production et mise en place de mesures correctives',
      improvement: 'Amélioration des processus et formation technique',
      optimization: 'Innovation et optimisation de la chaîne de valeur'
    }
  }

  return actions[domainShortName]?.[actionType] || 'Action personnalisée recommandée'
}

const generateNextSteps = (averageScore, criticalCount, strongCount) => {
  if (criticalCount >= 3) {
    return {
      immediate: 'Contactez immédiatement un consultant CEDEC pour un diagnostic d\'urgence',
      shortTerm: 'Mise en place d\'un plan de redressement sur 90 jours',
      mediumTerm: 'Suivi hebdomadaire et ajustements du plan d\'action'
    }
  }

  if (criticalCount >= 1) {
    return {
      immediate: 'Planifiez un entretien avec un expert CEDEC dans les 15 jours',
      shortTerm: 'Priorisez les actions sur les domaines critiques',
      mediumTerm: 'Évaluation des progrès et extension du plan d\'amélioration'
    }
  }

  if (averageScore >= 7) {
    return {
      immediate: 'Consultation stratégique pour optimiser votre excellence',
      shortTerm: 'Identification des opportunités de croissance',
      mediumTerm: 'Mise en place d\'une stratégie d\'innovation'
    }
  }

  return {
    immediate: 'Diagnostic approfondi avec un consultant CEDEC',
    shortTerm: 'Élaboration d\'un plan de développement personnalisé',
    mediumTerm: 'Accompagnement dans la mise en œuvre des améliorations'
  }
}
